<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4 border-radius-xl border-0 shadow-sm">
          <!-- UPDATED: Responsive flex classes for header to prevent overlap -->
          <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center bg-transparent gap-3">
            <div class="d-flex align-items-center gap-2">
              <h6 class="mb-0">Devices Management</h6>
              <!-- Visual Indicator of Config Installation Type -->
              <span v-if="installation_type_id" class="badge bg-soft-primary text-primary text-xxs border">
                Installation Type #{{ installation_type_id }}
              </span>
            </div>
            <div class="d-flex flex-wrap align-items-center gap-2 w-100 w-md-auto justify-content-between justify-content-md-end">
              <div class="btn-group btn-group-sm shadow-none">
                <button 
                  class="btn btn-sm mb-0" 
                  :class="viewMode === 'table' ? 'btn-theme-primary text-white' : 'btn-outline-theme'" 
                  @click="viewMode = 'table'"
                >
                  <i class="fas fa-table"></i>
                </button>
                <button 
                  class="btn btn-sm mb-0" 
                  :class="viewMode === 'card' ? 'btn-theme-primary text-white' : 'btn-outline-theme'" 
                  @click="viewMode = 'card'"
                >
                  <i class="fas fa-th-large"></i>
                </button>
              </div>

              <button class="btn btn-outline-secondary btn-sm mb-0" @click="showFilters = !showFilters">
                <i class="fas" :class="showFilters ? 'fa-times' : 'fa-search'"></i>
                <span class="d-none d-sm-inline">{{ showFilters ? ' Close Search' : ' Search' }}</span>
              </button>
              
              <button class="btn btn-theme-primary btn-sm mb-0 flex-grow-1 flex-md-grow-0" @click="openAddModal">
                ADD DEVICE
              </button>
            </div>
          </div>

          <TableSearchLimit v-model:limit="itemsPerPage" v-model:search="globalSearch" />

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="viewMode === 'table'" class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">Name / ID</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">{{ branchLabel }}</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Position</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Active Playlist</th>
                    <!-- Qty column hidden strictly for Medical Installation Type -->
                    <th v-if="!isMedical" class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Qty</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                  </tr>
                  <tr v-if="showFilters" class="bg-light">
                    <th class="px-4 py-2"><input v-model="filters.name" type="text" class="form-control form-control-sm" placeholder="Search Name/ID" /></th>
                    <th class="py-2"><input v-model="filters.branch" type="text" class="form-control form-control-sm text-center" :placeholder="'Search ' + branchLabel" /></th>
                    <th class="py-2"><input v-model="filters.position" type="text" class="form-control form-control-sm text-center" placeholder="Search Position" /></th>
                    <th></th>
                    <th v-if="!isMedical"></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <!-- UPDATED: Added highlight class if playlist_master_id is missing -->
                  <tr v-for="device in paginatedDevices" :key="device.id" :class="{'token-active-bg': device.show_token, 'playlist-not-assigned-bg': !device.playlist_master_id}">
                    <td class="ps-4">
                      <div class="d-flex align-items-center">
                        <h6 class="mb-0 text-sm">{{ device.device_name }}</h6>
                        <i v-if="device.show_token" class="fas fa-key ms-2 text-theme" style="font-size: 10px;" title="Token Display Enabled"></i>
                      </div>
                      <p class="text-xs text-secondary mb-0">ID: {{ device.device_id }}</p>
                    </td>
                   
                    <td class="text-center">
                      <soft-badge color="info" variant="gradient" size="sm">{{ getBranchName(device.branch_id) }}</soft-badge>
                    </td>
                    <td class="text-center"><p class="text-xs font-weight-bold mb-0">{{ device.device_position || 'N/A' }}</p></td>
                    <td class="text-center">
                      <span class="text-xs font-weight-bold text-dark">{{ getPlaylistName(device.playlist_master_id) }}</span>
                    </td>
                    <!-- Qty cell hidden strictly for Medical Installation Type -->
                    <td v-if="!isMedical" class="text-center"><p class="text-xs font-weight-bold mb-0">{{ device.number_of_devices || 1 }}</p></td>
                    <td class="align-middle text-center">
                      <a href="javascript:;" class="text-theme font-weight-bold text-xs me-3" @click="openInContent(device)" title="Open in Content Library">
                        <i class="fas fa-external-link-alt"></i>
                      </a>
                      <a href="javascript:;" class="text-secondary font-weight-bold text-xs me-3" @click="editDevice(device)">
                        <i class="fas fa-edit"></i>
                      </a>
                      <a href="javascript:;" class="text-danger font-weight-bold text-xs" @click="deleteDevice(device.id)">
                        <i class="fas fa-trash"></i>
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="px-3 px-md-4 pt-4">
              <div class="row">
                <!-- UPDATED: Added col-12 for full width on mobile -->
                <div v-for="device in paginatedDevices" :key="'card-' + device.id" class="col-12 col-md-6 col-lg-4 col-xl-3 mb-4">
                  <!-- UPDATED: Added highlight class if playlist_master_id is missing -->
                  <div class="card device-square-card shadow-sm border h-100" :class="{'token-active-card': device.show_token, 'playlist-not-assigned-card': !device.playlist_master_id}">
                    
                    <div v-if="device.show_token" class="token-floating-badge" title="Token Display Enabled">
                      <i class="fas fa-key text-white"></i>
                    </div>

                    <div class="card-body d-flex flex-column align-items-center justify-content-between text-center p-3">
                      
                      <div class="w-100 d-flex justify-content-between mb-2">
                        <div class="d-flex align-items-center gap-1">
                          <soft-badge color="info" variant="gradient" size="xs">{{ getBranchName(device.branch_id) }}</soft-badge>
                          <span 
                            class="badge text-white text-xxs font-weight-bolder"
                            :style="{
                              backgroundColor: (device.status || '').toLowerCase() === 'online' ? '#2dce89' : '#f5365c',
                              borderRadius: '0.375rem',
                              padding: '0.35em 0.65em'
                            }"
                          >
                            {{ (device.status || '').toLowerCase() === 'online' ? 'ONLINE' : 'OFFLINE' }}
                          </span>
                        </div>
                        <div>
                          <i class="fas fa-edit text-secondary text-xs cursor-pointer me-2" @click="editDevice(device)"></i>
                          <i class="fas fa-trash text-danger text-xs cursor-pointer" @click="deleteDevice(device.id)"></i>
                        </div>
                      </div>

                      <div class="my-auto w-100">
                        <div class="icon-circle mb-2 mx-auto" :class="device.show_token ? 'bg-theme' : 'bg-gray-100 theme-icon-bg-light'">
                          <i class="fas fa-tablet-alt" :class="device.show_token ? 'text-white' : 'text-theme'"></i>
                        </div>
                        <h6 class="text-dark text-sm font-weight-bold mb-1 text-uppercase">{{ device.device_name }}</h6>
                        <p class="text-xxs text-secondary mb-0">ID: {{ device.device_id }}</p>
                        <div class="mt-2 d-flex flex-wrap justify-content-center gap-1">
                           <span class="badge bg-light text-dark text-xxs border text-uppercase">POS: {{ device.device_position || 'N/A' }}</span>
                           <!-- Ensure playlist name synced from group appears here -->
                           <span class="badge bg-soft-primary text-primary text-xxs border text-uppercase">Playlist: {{ getPlaylistName(device.playlist_master_id) }}</span>
                        </div>
                      </div>

                      <div class="w-100 mt-3 d-flex gap-2">
                        <button class="btn btn-outline-theme btn-sm flex-grow-1 mb-0 py-2" @click="editDevice(device)">
                          VIEW DETAILS
                        </button>
                        <button class="btn btn-theme-info btn-sm mb-0 py-2 px-3" @click="openInContent(device)" title="Open Library">
                           <i class="fas fa-play text-white"></i>
                        </button>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL -->
    <div v-if="showModal" class="modal fade show" style="display: block; background-color: rgba(0,0,0,0.5);" tabindex="-1" role="dialog">
      <!-- UPDATED: Centered modal -->
      <div class="modal-dialog modal-lg modal-dialog-centered px-3" role="document">
        <div class="modal-content border-radius-xl">
          <div class="modal-header border-bottom">
            <h5 class="modal-title font-weight-bolder">{{ isEdit ? 'Update Device' : 'Add New Device' }}</h5>
            <button type="button" class="btn-close-theme" @click="showModal = false">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <!-- UPDATED: scrollable body for smaller screens -->
          <div class="modal-body p-3 p-md-4" style="max-height: 80vh; overflow-y: auto;">
            <form @submit.prevent="saveDevice">
              <div class="form-group">
                <label class="form-control-label">Device Name</label>
                <input v-model="form.device_name" type="text" class="form-control custom-input" placeholder="e.g. Lobby TV" />
                <small v-if="errors.device_name" class="text-danger">{{ errors.device_name }}</small>
              </div>

              <div class="row mt-2">
                <div class="col-md-6 col-12">
                  <div class="form-group position-relative">
                    <label class="form-control-label">Assign to {{ branchLabel }}</label>
                    <!-- Searchable Branch Selection -->
                    <div class="dropdown">
                      <input 
                        v-model="branchSearchText" 
                        type="text"
                        class="form-select custom-input text-dark font-weight-bolder" 
                        :placeholder="'Search or Select ' + branchLabel + '...'" 
                        @focus="isBranchDropdownOpen = true"
                        @blur="closeDropdownWithDelay('branch')"
                        style="color: #000 !important; font-size: 14px;"
                      />
                      <div v-if="isBranchDropdownOpen" class="dropdown-menu show w-100 border py-2 mt-1 bg-white" style="max-height: 200px; overflow-y: auto; z-index: 1051; border: 1px solid #333 !important;">
                        <button type="button" class="dropdown-item py-2 text-dark font-weight-bolder" @mousedown="selectBranch({id: null, branch_name: ''})">
                          None (Select a {{ branchLabel }})
                        </button>
                        <button 
                          v-for="branch in branches.filter(b => (b.branch_name || b.name || '').toLowerCase().includes((branchSearchText || '').toLowerCase()))" 
                          :key="branch.id" 
                          class="dropdown-item py-2 text-dark font-weight-bolder"
                          type="button"
                          @mousedown="selectBranch(branch)"
                          style="color: #000 !important; font-size: 14px !important; border-bottom: 1px solid #f0f0f0;"
                        >
                          {{ branch.branch_name || branch.name }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 col-12">
                  <div class="form-group position-relative">
                    <label class="form-control-label">Assign Playlist</label>
                    <!-- Searchable Playlist Assignment -->
                    <div class="dropdown">
                      <input 
                        v-model="playlistSearchText" 
                        type="text"
                        class="form-select custom-input text-dark font-weight-bolder" 
                        placeholder="Search or Select Playlist..." 
                        @focus="isPlaylistDropdownOpen = true"
                        @blur="closeDropdownWithDelay('playlist')"
                        style="color: #000 !important; font-size: 14px;"
                      />
                      <div v-if="isPlaylistDropdownOpen" class="dropdown-menu show w-100 border py-2 mt-1 bg-white" style="max-height: 200px; overflow-y: auto; z-index: 1051; border: 1px solid #333 !important;">
                        <button type="button" class="dropdown-item py-2 text-dark font-weight-bolder" @mousedown="selectPlaylist({id: null, playlist_name: 'No Playlist Assigned'})">
                          None (No Playlist Assigned)
                        </button>
                        <button 
                          v-for="playlist in playlists.filter(p => p.playlist_name.toLowerCase().includes((playlistSearchText || '').toLowerCase()))" 
                          :key="playlist.id" 
                          class="dropdown-item py-2 text-dark font-weight-bolder"
                          type="button"
                          @mousedown="selectPlaylist(playlist)"
                          style="color: #000 !important; font-size: 14px !important; border-bottom: 1px solid #f0f0f0;"
                        >
                          {{ playlist.playlist_name }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mt-2">
                <!-- Takes full width when Medical since Number of Devices is hidden -->
                <div :class="isMedical ? 'col-12' : 'col-md-6 col-12'">
                  <div class="form-group">
                    <label class="form-control-label">Device Position</label>
                    <select v-model="form.device_position" class="form-control custom-input">
                      <option value="">Select Position</option>
                      <option v-for="pos in positions" :key="pos.id" :value="pos.name">{{ pos.name }}</option>
                      <option value="Other">Other</option>
                    </select>
                    <input v-if="form.device_position === 'Other'" v-model="customPosition" type="text" class="form-control mt-2 custom-input" placeholder="Enter custom position" />
                  </div>
                </div>

                <!-- NUMBER OF DEVICES: STRICTLY HIDDEN FOR MEDICAL INSTALLATION TYPE -->
                <div v-if="!isMedical" class="col-md-6 col-12">
                  <div class="form-group">
                    <label class="form-control-label">Number of Devices</label>
                    <input v-model="form.number_of_devices" type="number" class="form-control custom-input" min="1" />
                  </div>
                </div>
              </div>

              <!-- CONNECTIVITY TOGGLE: STRICTLY HIDDEN FOR MEDICAL INSTALLATION TYPE -->
              <div v-if="!isMedical" class="row mt-3">
                <div class="col-md-12">
                  <div class="form-check form-switch ps-0">
                    <label class="form-control-label d-block">Device Connectivity</label>
                    <div class="d-flex align-items-center mt-2">
                      <input 
                        v-model="form.show_token" 
                        class="form-check-input ms-0 me-2 custom-checkbox" 
                        type="checkbox" 
                        id="showTokenCheck"
                      />
                      <label class="form-check-label mb-0 text-sm" for="showTokenCheck">
                        Show Token on Screen
                      </label>
                    </div>
                    <small class="text-secondary text-xs">If enabled, the device will display its connection token on the output screen.</small>
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-end mt-4 pt-3 border-top gap-2">
                <button type="button" class="btn btn-light btn-sm mb-0 flex-grow-1 flex-md-grow-0" @click="showModal = false">Cancel</button>
                <button type="submit" class="btn btn-theme-primary btn-sm mb-0 px-4 flex-grow-1 flex-md-grow-0">
                  <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                  {{ isEdit ? 'Update' : 'Save' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import SoftBadge from "@/components/SoftBadge.vue";
import TableSearchLimit from "@/components/TableSearchLimit.vue";
import Swal from 'sweetalert2';

export default {
  components: { SoftBadge, TableSearchLimit },
  data() {
    return {
      viewMode: 'card', 
      isLoading: false,
      apiBaseUrl: process.env.VUE_APP_API_BASE_URL ,
      devices: [],
      branches: [], 
      positions: [],
      playlists: [], // Store playlist data
      customPosition: "",
      showModal: false,
      isEdit: false,
      showFilters: false,
      currentId: null,
      globalSearch: "",
      itemsPerPage: 20,
      currentPage: 1,
      errors: {},
      filters: { name: "", hw_id: "", branch: "", position: "" },
      // Added search states for dropdowns
      isBranchDropdownOpen: false,
      isPlaylistDropdownOpen: false,
      branchSearchText: "",
      playlistSearchText: "",
      installation_type_id: null,
      configs: {},
      form: { 
        device_name: "", device_id: "", device_hw_id: "", 
        branch_id: null, playlist_master_id: null, 
        device_position: "", number_of_devices: 1, 
        latitude: "", longitude: "",
        show_token: false,
        user_id: null
      }
    };
  },
  computed: {
    branchLabel() {
      return this.isMedical ? 'Department' : 'Branch';
    },
    // Dynamic detection for Medical Installation Type
    isMedical() {
      const instType = (localStorage.getItem('installationType') || '').toLowerCase();
      const instId = String(this.installation_type_id || localStorage.getItem('installation_type_id') || '').trim();
      const configName = String(this.configs?.installation_type_name || this.configs?.installation_type || '').toLowerCase();
      return (
        instType.includes('medical') ||
        ['2', '5', '7', '27'].includes(instId) ||
        configName.includes('medical')
      );
    },
    filteredDevices() {
      return this.devices.filter(device => {
        const branchName = this.getBranchName(device.branch_id);
        const playlistName = this.getPlaylistName(device.playlist_master_id);
        const globalMatch = !this.globalSearch || [
          device.device_name, device.device_id, device.device_hw_id, device.device_position, branchName, playlistName
        ].some(val => (val || "").toLowerCase().includes(this.globalSearch.toLowerCase()));
        
        const nameMatch = (device.device_name || "").toLowerCase().includes(this.filters.name.toLowerCase()) || (device.device_id || "").toLowerCase().includes(this.filters.name.toLowerCase());
        const hwMatch = (device.device_hw_id || "").toLowerCase().includes(this.filters.hw_id.toLowerCase());
        const branchMatch = branchName.toLowerCase().includes(this.filters.branch.toLowerCase());
        const positionMatch = (device.device_position || "").toLowerCase().includes(this.filters.position.toLowerCase());
        return globalMatch && nameMatch && hwMatch && branchMatch && positionMatch;
      });
    },
    paginatedDevices() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredDevices.slice(start, start + this.itemsPerPage);
    }
  },
  mounted() { 
    this.fetchData(); 
  },
  methods: {
    // New selection methods for searchable dropdowns
    selectBranch(branch) {
      this.form.branch_id = branch.id;
      this.branchSearchText = branch.id ? (branch.branch_name || branch.name) : "";
      this.isBranchDropdownOpen = false;
    },
    selectPlaylist(playlist) {
      this.form.playlist_master_id = playlist.id;
      this.playlistSearchText = playlist.id ? playlist.playlist_name : "";
      this.isPlaylistDropdownOpen = false;
    },
    closeDropdownWithDelay(type) {
      setTimeout(() => {
        if (type === 'branch') this.isBranchDropdownOpen = false;
        if (type === 'playlist') this.isPlaylistDropdownOpen = false;
      }, 200);
    },

    async fetchConfig() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/config`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        if (res.data && res.data.data) {
          this.configs = res.data.data;
          this.installation_type_id = res.data.data.installation_type_id || null;
        }
      } catch (e) {
        console.error("Error fetching config:", e);
      }
    },

    async fetchData() {
       // Await config first to ensure installation_type_id is resolved
       await this.fetchConfig();
       // Await playlists first to ensure IDs map to names on load
       await this.fetchPlaylists();
       await this.fetchBranches(); 
       await this.fetchPositions(); 
       await this.fetchDevices(); 
    },
    openInContent(device) {
      this.$router.push({
        path: '/content', 
        query: { deviceId: device.id }
      });
    },
    generateRandomDeviceId() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let result = "";
      for (let i = 0; i < 21; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },
    validateFields() {
      this.errors = {};
      if (!this.form.device_name) this.errors.device_name = "Device name is required.";
      return Object.keys(this.errors).length === 0;
    },
    async fetchPlaylists() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/playlists`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        this.playlists = res.data;
      } catch (e) { console.error("Error fetching playlists", e); }
    },
    async fetchPositions() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/device-position`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        this.positions = res.data.data;
      } catch (e) { console.error(e); }
    },

    // UPDATED: Robust Role-based check for 'admin', 'operator', and Config Installation Type
// UPDATED: Robust Role-based check for 'admin', 'operator', and Config Installation Type
    async fetchDevices() {
      try {
        const rawRole = localStorage.getItem('userRole') || '';
        const role = rawRole.toLowerCase().trim();
        const userId = localStorage.getItem('id');

        let url = `${this.apiBaseUrl}/v1/admin/user-device-list`;
        const params = [];
        
        // Scope to user/tenant ID for operators and tenant admins (only Super Admin bypasses)
        if (!role.includes('superadmin') && !role.includes('reception') && userId) {
          params.push(`user_id=${userId}`);
        }

        // Attach installation_type_id from config if available
        if (this.installation_type_id) {
          params.push(`installation_type_id=${this.installation_type_id}`);
        }

        if (params.length > 0) {
          url += `?${params.join('&')}`;
        }

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        this.devices = res.data;
      } catch (e) { console.error(e); }
    },

    // UPDATED: Added same Role-based check for Branches to ensure Operator and Tenant Admin only see their own branches
    async fetchBranches() {
      try {
        const rawRole = localStorage.getItem('userRole') || '';
        const role = rawRole.toLowerCase().trim();
        const userId = localStorage.getItem('id');

        let url = `${this.apiBaseUrl}/v1/admin/branches`;
        if (['operator', 'opertor'].includes(role) && userId) {
          url += `?user_id=${userId}`;
        }

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.branches || res.data?.result || []);
        this.branches = data;
      } catch (e) { console.error(e); }
    },
    getBranchName(id) {
      const branch = this.branches.find(b => b.id == id);
      return branch ? (branch.branch_name || branch.name) : `No ${this.branchLabel}`;
    },
    getPlaylistName(id) {
      if (!id) return '--';
      const playlist = this.playlists.find(p => p.id == id);
      return playlist ? playlist.playlist_name : '--';
    },
    openAddModal() {
      this.isEdit = false;
      this.errors = {};
      this.customPosition = "";
      this.branchSearchText = "";
      this.playlistSearchText = "";
      this.form = { 
        device_name: "", 
        device_id: this.generateRandomDeviceId(), 
        device_hw_id: "", 
        branch_id: null, 
        playlist_master_id: null, 
        device_position: "", 
        number_of_devices: 1, 
        latitude: "", 
        longitude: "",
        show_token: false,
        user_id: localStorage.getItem('id') || null
      };
      this.showModal = true;
    },
    editDevice(device) {
      this.isEdit = true;
      this.errors = {};
      this.currentId = device.id;
      
      // Sync searchable text UI
      this.branchSearchText = this.getBranchName(device.branch_id);
      this.playlistSearchText = this.getPlaylistName(device.playlist_master_id);
      if (this.branchSearchText === 'No Branch' || this.branchSearchText === `No ${this.branchLabel}`) this.branchSearchText = "";
      if (this.playlistSearchText === '--') this.playlistSearchText = "";

      const positionExists = this.positions.find(p => p.name === device.device_position);
      
      let initialPos = "";
      if (device.device_position && !positionExists) {
        initialPos = "Other";
        this.customPosition = device.device_position;
      } else {
        initialPos = device.device_position;
        this.customPosition = "";
      }

      this.form = { 
        device_name: device.device_name, 
        device_id: device.device_id, 
        device_hw_id: device.device_hw_id, 
        branch_id: device.branch_id, 
        playlist_master_id: device.playlist_master_id, 
        device_position: initialPos, 
        number_of_devices: device.number_of_devices || 1,
        latitude: device.device_location?.coordinates[1] || "", 
        longitude: device.device_location?.coordinates[0] || "",
        show_token: device.show_token ? true : false,
        user_id: device.user_id
      };
      this.showModal = true;
    },
    async saveDevice() {
      if (!this.validateFields()) return;
      this.isLoading = true;
      
      const payload = { ...this.form };
      if (payload.device_position === 'Other') {
        payload.device_position = this.customPosition;
      }

      // If installation type is Medical, strictly enforce single device and disable token display
      if (this.isMedical) {
        payload.number_of_devices = 1;
        payload.show_token = false;
      }

      const url = this.isEdit ? `${this.apiBaseUrl}/v1/admin/device/update/${this.currentId}` : `${this.apiBaseUrl}/v1/admin/device/add`;
      try {
        await axios[this.isEdit ? 'put' : 'post'](url, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        this.showModal = false;
        Swal.fire('Success', `Device ${this.isEdit ? 'updated' : 'added'} successfully!`, 'success');
        this.fetchDevices();
      } catch (e) {
        Swal.fire('Error', 'Could not save device details.', 'error');
      } finally { this.isLoading = false; }
    },
    async deleteDevice(id) {
      const result = await Swal.fire({ title: 'Are you sure?', text: "You won't be able to revert this!", icon: 'warning', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, delete it!' });
      if (result.isConfirmed) {
        try {
          await axios.delete(`${this.apiBaseUrl}/v1/admin/device/delete/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
          this.fetchDevices();
          Swal.fire('Deleted!', 'Device has been removed.', 'success');
        } catch (e) { Swal.fire('Error', 'Failed to delete.', 'error'); }
      }
    }
  }
};
</script>

<style scoped>
/* ==========================
   DYNAMIC THEME SYNC
========================== */
.text-theme { color: var(--bs-primary) !important; }
.bg-theme { background-color: var(--bs-primary) !important; color: white !important; }

.btn-theme-primary {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: 1px solid var(--bs-primary) !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}
.btn-theme-primary:hover { filter: brightness(0.9); transform: translateY(-1px); }

.btn-outline-theme {
  border: 1px solid var(--bs-primary) !important;
  color: var(--bs-primary) !important;
  background: transparent;
}
.btn-outline-theme:hover { background-color: var(--bs-primary) !important; color: white !important; }

.btn-theme-info {
  background-color: #11cdef !important; 
  border: none;
  transition: all 0.2s;
}
.btn-theme-info:hover { filter: brightness(0.9); }

.btn-close-theme {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  opacity: 0.9;
}
.btn-close-theme:hover { opacity: 1; }

.custom-input:focus {
  border-color: var(--bs-primary) !important;
  box-shadow: 0 0 0 2px rgba(var(--bs-primary-rgb), 0.1) !important;
}

.custom-checkbox:checked {
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.theme-icon-bg-light { background-color: rgba(0,0,0,0.03) !important; }

/* ==========================
   HIGHLIGHTING LOGIC
========================== */
.token-active-bg {
  background-color: rgba(var(--bs-primary-rgb), 0.04) !important;
}

.token-active-card {
  border: 2px solid var(--bs-primary) !important;
}

.token-floating-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: var(--bs-primary);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.bg-soft-primary {
  background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
}

/* --- ADDED: HIGHLIGHT FOR MISSING PLAYLIST --- */
.playlist-not-assigned-bg {
  background-color: rgba(255, 193, 7, 0.05) !important; /* Subtle yellow warning background */
}

.playlist-not-assigned-card {
  border: 1px dashed #ffc107 !important; /* Dashed yellow warning border */
}

/* ==========================
   LAYOUT & CARDS
========================== */
.device-square-card {
  aspect-ratio: 1 / 1; 
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative; 
}
.device-square-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 15px rgba(0,0,0,0.1) !important;
}
.icon-circle {
  width: 45px; height: 45px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem;
}
.cursor-pointer { cursor: pointer; }
.form-control-label { font-size: 0.75rem; font-weight: 800; color: #344767; text-transform: uppercase; letter-spacing: 0.5px; }

/* --- ADDED MOBILE RESPONSIVE CSS --- */
@media (max-width: 768px) {
  .card-header {
    padding-bottom: 20px !important;
  }
  .device-square-card {
    aspect-ratio: auto;
    min-height: 250px;
  }
}
</style>